import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Applications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch applications on component mount
  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const data = await adminService.getApplications();
      
      if (Array.isArray(data)) {
        setApplications(data);
        setError(null);
        console.log('Applications loaded:', data.length);
      } else {
        console.error('Unexpected data format:', data);
        setApplications([]);
        setError('Invalid data format received from server');
        toast.error('Invalid data format received');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setApplications([]);
      setError(err.message);
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demonstration
  const mockApplications = [
    {
      id: 1,
      campaign_title: 'Summer Sale 2024',
      influencer_name: 'John Doe',
      influencer_email: 'john@example.com',
      status: 'pending',
      applied_at: '2024-01-20T10:00:00.000Z',
      platform: 'Instagram',
      followers: 15000,
      engagement_rate: 3.2,
    },
    {
      id: 2,
      campaign_title: 'Holiday Special',
      influencer_name: 'Jane Smith',
      influencer_email: 'jane@example.com',
      status: 'accepted',
      applied_at: '2024-01-19T15:30:00.000Z',
      platform: 'TikTok',
      followers: 25000,
      engagement_rate: 4.1,
    },
  ];

  // Use real data if available, otherwise fallback to mock
  const displayApplications = applications.length > 0 ? applications : mockApplications;
  
  const filteredApplications = displayApplications.filter(application => {
    const matchesSearch = (
      (application.campaign_title && application.campaign_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (application.influencer_name && application.influencer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (application.influencer_email && application.influencer_email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const matchesStatus = selectedStatus === 'all' || application.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      under_review: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace('_', ' ') || 'Unknown'}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'under_review':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    if (!['accepted', 'rejected', 'pending', 'under_review'].includes(newStatus)) {
      toast.error('Invalid status value');
      return;
    }
    try {
      // Call backend API to update application status
      const response = await adminService.updateApplicationStatus(applicationId, newStatus);
      if (response && response.application && response.application.status === newStatus) {
        toast.success(`Application ${newStatus} successfully!`);
        fetchApplications();
      } else {
        toast.error('Status not updated. Check backend response.');
        console.error('Unexpected response:', response);
      }
    } catch (error) {
      toast.error('Failed to update application status');
      if (error.response) {
        console.error('API error:', error.response.data);
      } else {
        console.error('Error:', error);
      }
    }
  };

  // View handler (for now, just alert or log)
  const handleViewApplication = (application) => {
    // You can replace this with a modal or navigation
    alert(`Application ID: ${application.id}\nCampaign ID: ${application.campaign_id || 'N/A'}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Applications</h1>
          <p className="text-gray-600">
            {error ? 'Showing demo data (API connection failed)' : 'Review and manage campaign applications'}
          </p>
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {displayApplications.filter(a => a.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <AlertCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Under Review</p>
              <p className="text-2xl font-bold text-blue-600">
                {displayApplications.filter(a => a.status === 'under_review').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-green-600">
                {displayApplications.filter(a => a.status === 'accepted').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-600">
                {displayApplications.filter(a => a.status === 'rejected').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading applications...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <p className="text-gray-600 mb-4">Showing demo data below. Click retry to attempt fetching real data again.</p>
            <button onClick={fetchApplications} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by campaign, influencer name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
            <button className="btn-secondary flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      {!loading && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Application
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Influencer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Metrics
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-700">
                            {application.influencer_name?.charAt(0) || 'I'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">#{application.id}</div>
                          <div className="text-sm text-gray-500">
                            {application.applied_at ? new Date(application.applied_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {application.campaign_title || 'Untitled Campaign'}
                      </div>
                      <div className="text-xs text-gray-500">
                        Campaign ID: {application.campaign_id || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {application.influencer_name || 'Unknown Influencer'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {application.influencer_email || 'No email'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {application.platform || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{application.followers?.toLocaleString() || '0'} followers</div>
                        <div className="text-gray-500">{application.engagement_rate || '0'}% engagement</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(application.status)}
                        {getStatusBadge(application.status)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {application.applied_at ? new Date(application.applied_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="text-primary-600 hover:text-primary-900" title="View Details" onClick={() => handleViewApplication(application)}>
                          <Eye className="h-4 w-4" />
                        </button>
                        {application.status === 'pending' && (
                          <>
                            <button 
                              onClick={() => handleStatusUpdate(application.id, 'accepted')}
                              className="text-green-600 hover:text-green-900"
                              title="Accept"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleStatusUpdate(application.id, 'rejected')}
                              className="text-red-600 hover:text-red-900"
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button className="btn-secondary">Previous</button>
              <button className="btn-secondary">Next</button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredApplications.length}</span> of{' '}
                  <span className="font-medium">{filteredApplications.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button className="btn-secondary rounded-l-md">Previous</button>
                  <button className="btn-secondary rounded-r-md">Next</button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredApplications.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <AlertCircle className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'No campaign applications have been submitted yet.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default Applications;
